// ─────────────────────────────────────────────
//  DailyAssignmentPage — the paperless OR day (Phase 4, D-10)
//
//  Assembles the full daily workflow in UI-SPEC section order:
//  header (title + date + roster builder) → AssignmentGrid →
//  AvailabilitySection → BreakEnginePanel per rostered provider →
//  "Add Staff from Another Facility". Restyled dark→light onto the
//  Phase-1 token theme. Administrators + Schedulers only (server rules are
//  the real boundary; this guard is the UX layer).
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavMenu from "../components/NavMenu";
import Avatar from "../components/Avatar";
import SignOutButton from "../components/SignOutButton";
import AccessDenied from "../components/AccessDenied";
import tokens from "../theme/tokens";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useAlertCenter } from "../context/AlertContext";
import useNowMinutes from "../hooks/useNowMinutes";
import { useDailyRoster, rosterDateKey } from "../hooks/useDailyRoster";
import { nextDayKey } from "../domain/importDate";
import { useAicByFacility } from "../hooks/useAicByFacility";
import { useFacilities } from "../hooks/useFacilities";
import { useIsMobile } from "../hooks/useIsMobile";
import { useStaff } from "../hooks/useStaff";
import { useLocations } from "../hooks/useLocations";
import {
  setDailyRoster,
  listenToAssignments,
  listenToRooms,
  hideTemplateRoom,
  unhideTemplateRoom,
  deleteAssignmentRow,
  clearRoom,
  createAssignment,
  addStaff,
  saveSheetImport, pruneSheetImportsKeepLast,
  listenToSheetImports,
  pruneStaleSheetImports,
} from "../firebase/firestore";
import { selectRecent, isGlobalImportRecord } from "../domain/sheetImports";
import { initialBreaks } from "../domain/breaks";
import { breakBoardRows } from "../hooks/useBreakBoardRows";
import { mergeSecondProviders, secondProviderFields, SECOND_PROVIDER_ROLES } from "../domain/secondProvider";
import Button from "../components/Button";
import AssignmentGrid from "../components/AssignmentGrid";
import AvailabilitySection from "../components/AvailabilitySection";
import BreakBoard from "../components/BreakBoard";
import BreakEnginePanel from "../components/BreakEnginePanel";
import Modal from "../components/Modal";
import DepartureFlow from "../components/DepartureFlow";
import RemoveFromDay from "../components/RemoveFromDay";
import CrossFacilityPicker from "../components/CrossFacilityPicker";
import MdCallPanel from "../components/MdCallPanel";
import CrnaRosterPanel from "../components/CrnaRosterPanel";
import { otherFacilities } from "../domain/facilities";
import { useFacility } from "../hooks/useFacility";
import { resolveFacilityRules } from "../domain/facilityRules";
import { crnaRoomConflicts } from "../domain/crnaRoomGuard";
import { testDoctors, testProviders } from "../config/testUsers";
import AssignmentImport from "../components/AssignmentImport";
import GlobalSheetImport from "../components/GlobalSheetImport";
import { linkCrnaChains } from "../features/sheet-import/linkCrnaChains";

const { color, font, space } = tokens;

export default function DailyAssignmentPage() {
  const { user, isDoctor, isAdmin, canManageDailyList, testingMode } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useIsMobile(640);
  const facilityId = user?.activeFacilityId;
  // ⚠️ DECLARED HERE, ABOVE THE ACCESS GUARD. This page returns early for a
  // non-administrator (line ~430), so a hook added below that point would break
  // the rules of hooks on every refused render.
  //
  // A break REQUEST lives in a BREAK_REQUEST alert, never in `room.breaks`, so
  // the Break Engine below cannot see one without this subscription.
  const { alerts } = useAlertCenter();
  const breakBoardNowMin = useNowMinutes();

  const [date, setDate] = useState(rosterDateKey());
  // Campus leaders for the MD Call colour-coding (WC AIC red / Tampa DOD blue /
  // Carrollwood AIC green). Daily List is a roster-editor surface, so every
  // viewer here has the cross-facility board read rules allow.
  const facilityBoards = useAicByFacility(date, { enabled: canManageDailyList });
  // Live facility docs name and colour each campus's leader tag in MD Call.
  const allFacilities = useFacilities();
  const { roster } = useDailyRoster(facilityId, date);

  // Source of truth for the people pool = the Anesthesia Staff master list
  // (Facility Settings). The roster below selects the day's subset from it,
  // which then cascades to the grid, board, dropdowns and filters.
  const { doctors, providers } = useStaff();
  // Locations defined in Facility Settings — drive the grid's section headers,
  // matching the OR Board so both views show the same structure.
  const { locations } = useLocations(facilityId);
  // Phase 5.4 — per-facility rules (float permission for the picker below).
  const { facility: facilityDoc } = useFacility(facilityId);
  const facilityRules = resolveFacilityRules(facilityDoc);
  const [assignments, setAssignments] = useState([]);
  const [rooms, setRooms] = useState([]);
  // Recent imported sheets (last 3, newest first) for the Import section.
  const [recentImports, setRecentImports] = useState([]);
  const [aicPick, setAicPick] = useState("");
  const [supPick, setSupPick] = useState("");
  const [crnaPick, setCrnaPick] = useState("");
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Break detail modal — set to a provider object to open, null to close.
  const [breakDetail, setBreakDetail] = useState(null);

  // Single ticking clock for the whole page — drives every go-home alert.
  // 60s resolution is plenty for a 30-minute window; cleared on unmount.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  // The day's grid rows.
  useEffect(() => {
    if (!facilityId || !date) return;
    const unsub = listenToAssignments(facilityId, date, (snap) => {
      setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [facilityId, date]);

  // Live rooms — the persisted break state per provider lives on room docs.
  useEffect(() => {
    if (!facilityId) return;
    const unsub = listenToRooms(facilityId, (snap) => {
      setRooms(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [facilityId]);

  // Recent imported sheets + daily cleanup. Prune anything older than 3 days on
  // mount (client-side — no scheduled Cloud Function / Blaze needed), then keep
  // the last 3 live for the Import section's "recent files" list.
  useEffect(() => {
    if (!facilityId) return;
    pruneStaleSheetImports(facilityId, Date.now()).catch((e) =>
      console.error("[DailyAssignmentPage] prune imports failed:", e)
    );
    const unsub = listenToSheetImports(
      facilityId,
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data();
          let snapshot = {};
          try { snapshot = JSON.parse(data.snapshotJson || "{}"); } catch { snapshot = {}; }
          return {
            id: d.id,
            filename: data.filename,
            source: data.source,
            dateKey: data.dateKey,
            importedAt: data.importedAt,
            snapshot,
          };
        });
        // Facility-only history: global/scheduled records live in the 🌐 list.
        setRecentImports(selectRecent(list.filter((r) => !isGlobalImportRecord(r)), 3));
      },
      (e) => console.error("[DailyAssignmentPage] listen imports failed:", e)
    );
    return unsub;
  }, [facilityId]);

  const isToday = date === rosterDateKey();
  const actor = { uid: user?.uid, name: user?.name };

  // Staff pools — testing mode injects the code-defined testing accounts into
  // every dropdown; production mode hides them completely.
  const doctorPool = testingMode ? [...doctors, ...testDoctors()] : doctors;
  const providerPool = testingMode ? [...providers, ...testProviders()] : providers;

  // Location-based filtering for the roster-builder dropdowns: each facility
  // sees ONLY its own staff. Membership = staff.primaryFacilityId (set in
  // Facility Settings and on import) or an explicit facilityIds list.
  // Code-defined testing accounts carry neither and stay visible in testing
  // mode. Cross-facility staffing still goes through the CrossFacilityPicker.
  const belongsHere = (p) =>
    p.isTestAccount ||
    p.primaryFacilityId === facilityId ||
    (Array.isArray(p.facilityIds) && p.facilityIds.includes(facilityId));
  const facilityDoctors = doctorPool.filter(belongsHere);
  const facilityCrnas = providerPool.filter(belongsHere);

  // Persist a partial change to the day's roster (merge keeps other fields).
  const persist = async (patch, successMsg) => {
    if (!canManageDailyList) { toast.error("This page is for Administrators and Schedulers only."); return; }
    setSaving(true);
    try {
      await setDailyRoster(facilityId || "default", date, patch, user?.uid);
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      console.error("Save daily list failed:", err);
      toast.error("Couldn't save. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const setAIC = (id) => {
    const doc = doctorPool.find((d) => d.id === id);
    persist({ aicId: id || null, aicName: doc?.name || "" },
      doc ? `${doc.name} is the AIC for ${date}.` : "AIC cleared.");
  };

  const addSupervisor = () => {
    if (!supPick) return;
    if (roster.supervisors.some((s) => s.id === supPick)) { toast.info("Already on today's list."); return; }
    const d = doctorPool.find((x) => x.id === supPick);
    if (!d) return;
    persist({ supervisors: [...roster.supervisors, { id: d.id, name: d.name }] }, `${d.name} added.`);
    setSupPick("");
  };

  const addCrna = () => {
    if (!crnaPick) return;
    if (roster.crnas.some((c) => c.id === crnaPick)) { toast.info("Already on today's list."); return; }
    const p = providerPool.find((x) => x.id === crnaPick);
    if (!p) return;
    persist({ crnas: [...roster.crnas, { id: p.id, name: p.name, subRole: p.subRole || "crna" }] }, `${p.name} added.`);
    setCrnaPick("");
  };

  // Reset the WHOLE day: the roster (AIC + supervisors + CRNAs + call lists +
  // departures), every assignment row, AND every room's live board state.
  // Two-click confirm (no modal) since it's easily rebuilt. The room DOCS are
  // preserved (Setup → Room Management config); clearRoom only wipes the live
  // assignment/break state so the board and grid both read empty afterwards.
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const clearRoster = async () => {
    if (!canManageDailyList) { toast.error("This page is for Administrators and Schedulers only."); return; }
    setClearing(true);
    try {
      await Promise.all([
        ...assignments.map((a) => deleteAssignmentRow(a.id)),
        ...rooms.map((r) => clearRoom(r.id)),
      ]);
      await setDailyRoster(facilityId || "default",
        date,
        { aicId: null, aicName: "", supervisors: [], crnas: [], crnaCallList: [], mdCallList: [], mdCallHome: [], locumLeaveTimes: {}, departed: {}, hiddenRooms: [] },
        user?.uid);
      toast.success(`Cleared the roster, ${assignments.length} assignment row${assignments.length === 1 ? "" : "s"}, and reset ${rooms.length} room${rooms.length === 1 ? "" : "s"} for ${date}.`);
    } catch (err) {
      console.error("Clear roster failed:", err);
      toast.error("Couldn't fully clear the day. Check your connection and try again.");
    } finally {
      setClearing(false);
      setConfirmClear(false);
    }
  };

  // Patch a per-day field onto a roster member (Home Time for CRNAs, Call order for MDs).
  // Uses the same map-with-merge pattern as addSupervisor/addCrna — replaces the whole array
  // so no members are dropped (matches the plan's integrity note in the threat model).
  // UPSERT: a doctor/CRNA picked straight from a grid dropdown may not be on
  // today's roster list yet — add them so the patch isn't silently lost.
  // Privilege note: this writes to daily_boards only (doctor-editable via Firestore rules);
  // it does NOT call updateUser, which is admin-only for writes to other users.
  const onMemberPatch = (listKey, memberId, patch) => {
    const list = roster[listKey] || [];
    if (list.some((m) => m.id === memberId)) {
      persist({ [listKey]: list.map((m) => m.id === memberId ? { ...m, ...patch } : m) });
      return;
    }
    const pool = listKey === "supervisors" ? doctorPool : providerPool;
    const profile = pool.find((p) => p.id === memberId);
    const member = {
      id: memberId,
      name: profile?.name || "",
      ...(listKey === "crnas" ? { subRole: profile?.subRole || "crna" } : {}),
      ...patch,
    };
    persist({ [listKey]: [...list, member] });
  };

  // Apply AI-imported draft rows to the live board (AI-SPEC §6 guardrail path).
  // Called ONLY when the AIC clicks "Confirm / Apply to board" in AssignmentImport.
  // Uses ONLY existing write paths — no new Firestore write function added.
  const onApplyRows = async (draftRows, opts = {}) => {
    if (!canManageDailyList) { toast.error("This page is for Administrators and Schedulers only."); return; }
    const unassignedCrnas = Array.isArray(opts.unassigned) ? opts.unassigned : [];
    const mdCall = Array.isArray(opts.mdCall) ? opts.mdCall : null;
    const errs = [];

    // Accumulate ALL roster upserts and persist once at the end. Calling
    // onMemberPatch per row races on a stale `roster` closure + one write each,
    // so only the last doctor/CRNA survived — earlier attendings lost their
    // call order (e.g. CHO/HUA showed no call number). Build the merged arrays
    // locally, then a single setDailyRoster keeps every member's number.
    const supervisors = [...(roster.supervisors || [])];
    const crnas = [...(roster.crnas || [])];
    // Who holds a seat somewhere today — an orientee in one room who runs
    // another IS cover, so the flag is cleared for them before the write.
    const seatIds = new Set();
    let aicId = null;
    const upsert = (list, pool, id, fallbackName, patch) => {
      const i = list.findIndex((m) => m.id === id);
      if (i >= 0) { list[i] = { ...list[i], ...patch }; return; }
      const profile = pool.find((p) => p.id === id);
      list.push({
        id,
        name: profile?.name || fallbackName || "",
        ...(pool === providerPool ? { subRole: profile?.subRole || "crna" } : {}),
        ...patch,
      });
    };

    // Multi-room CRNAs auto-route by start time: the earlier case links to
    // the later one ("Next Room: X" / "Coming from: Y" on the two cards).
    const chainedRows = linkCrnaChains(draftRows, rooms);
    for (const row of chainedRows) {
      try {
        // Persist the assignment row (same data shape as AssignmentGrid.saveRow).
        const data = {
          facilityId,
          date,
          orRoom: row.orRoom || "",
          surgeon: row.surgeon || "",
          procedure: row.procedure || "",
          specialty: row.specialty || "",
          // On-call (24h) CRNA covers 7 AM → 7 AM next day; show 07:00 / 07:00 on
          // the card when the sheet gave no explicit case time.
          startTime: row.startTime || (row.onCall ? "07:00" : ""),
          endTime: row.endTime || (row.onCall ? "07:00" : ""),
          attendingId: row.attendingId || null,
          attendingName: row.attendingName || "",
          ahpId: row.ahpId || null,
          ahpName: row.ahpName || "",
          ahpShiftCode: row.ahpShiftCode ?? null,
          ahpDepartTime: row.ahpDepartTime || "",
          // ⚠️ THE SECOND PERSON IN THE ROOM, on THIS path too. `buildBoardWrites`
          // (the scheduled import) has carried the orienting pair since it
          // shipped; this hand-applied path did not, so the same sheet produced
          // a board that knew about two CRNAs or one depending on who pressed
          // the button. Half a converted pipeline loses the feature in silence.
          ...secondProviderFields({
            id: row.orienteeId,
            name: row.orienteeName,
            shiftCode: row.orienteeShiftCode,
            departTime: row.orienteeDepartTime,
            role: row.secondProviderRole || SECOND_PROVIDER_ROLES.ORIENTING,
          }),
          crnaReliefId: row.crnaReliefId || null,
          crnaReliefName: "",
          mdReliefId: row.mdReliefId || null,
          mdReliefName: "",
          // On-call (24h / full-shift) CRNA in a real room (e.g. OB · Grace Elaro
          // 24): no case time; board/timeline/roster render it as full coverage.
          isOnCall: Boolean(row.onCall),
          nextRoomId: row.nextRoomId || null,
          nextRoomName: row.nextRoomName || "",
          source: row.source || "ai-import",
        };
        await createAssignment(data);

        // Stage roster upserts (attach the number only when the sheet had one).
        if (row.attendingId) {
          upsert(supervisors, doctorPool, row.attendingId, row.attendingName,
            row.callOrder != null ? { callOrder: row.callOrder } : {});
        }
        if (row.ahpId) {
          seatIds.add(row.ahpId);
          upsert(crnas, providerPool, row.ahpId, row.ahpName,
            row.ahpShiftCode != null ? { shiftCode: row.ahpShiftCode } : {});
        }
        // An orientee joins the day's roster so the AIC can move them, the break
        // board can list them and messaging can reach them — flagged `orienting`
        // because the roster is also the staffing pool and they are present, not
        // cover. Same rule and same flag as buildBoardWrites.
        if (row.orienteeId) {
          const role = row.secondProviderRole || SECOND_PROVIDER_ROLES.ORIENTING;
          upsert(crnas, providerPool, row.orienteeId, row.orienteeName, {
            orienting: role === SECOND_PROVIDER_ROLES.ORIENTING,
            secondProviderRole: role,
            // §0.1 (owner, 2026-08-20): a second CRNA in a room IS cover.
            // Written explicitly because this is the one line a future
            // "students do not count" rule flips.
            countsAsCover: true,
            ...(row.orienteeShiftCode != null ? { shiftCode: row.orienteeShiftCode } : {}),
          });
        }
        if (row.isAIC && row.attendingId) aicId = row.attendingId;
      } catch (err) {
        console.error("[onApplyRows] row failed:", err);
        errs.push(row.orRoom || "?");
      }
    }

    // Unassigned bench (e.g. WC TBD · Jim Slone 3): add to the day's CRNA roster
    // with NO room assignment, so they appear in the roster + the board's
    // Unassigned section. Skip any without a resolved id (added via newStaff flow).
    for (const u of unassignedCrnas) {
      if (!u || !u.ahpId) continue;
      upsert(crnas, providerPool, u.ahpId, u.name,
        u.shiftCode != null ? { shiftCode: u.shiftCode } : (u.shiftNumber != null ? { shiftCode: String(u.shiftNumber) } : {}));
    }

    for (let i = 0; i < crnas.length; i += 1) {
      if (crnas[i].orienting && seatIds.has(crnas[i].id)) {
        const { orienting, secondProviderRole, ...rest } = crnas[i];
        crnas[i] = rest;
      }
    }

    // ONE roster write carrying every staged member + the AIC + the MD call list
    // (facility-wide attendings parsed from the sheet, for the MD Call panel).
    const aicDoc = aicId ? doctorPool.find((d) => d.id === aicId) : null;
    await persist({
      supervisors,
      crnas,
      ...(aicId ? { aicId, aicName: aicDoc?.name || "" } : {}),
      ...(mdCall ? { mdCallList: mdCall } : {}),
    });

    if (errs.length === 0) {
      toast.success(`Applied ${draftRows.length} imported row${draftRows.length === 1 ? "" : "s"} to the board.`);
    } else {
      toast.error(`Applied with errors on: ${errs.join(", ")}. Check those rows manually.`);
    }

    // Warn (don't block) when a non-OB CRNA landed in more than one room. OB
    // CRNAs may cover several; everyone else should hold one. The AIC can fix or
    // override on the grid below (the grid shows the same warning persistently).
    const conflicts = crnaRoomConflicts(draftRows, rooms);
    if (conflicts.length) {
      const who = conflicts.map((c) => `${c.ahpName || "a CRNA"} (${c.rooms.join(" + ")})`).join("; ");
      toast.warning(`Double-booked non-OB CRNA: ${who}. Review the grid.`);
    }
  };

  if (!canManageDailyList) {
    return <AccessDenied body="Daily Assignments is for administrators and schedulers. The board shows the same day, and My Rooms shows yours." />;
  }

  const sortByLast = (a, b) => (a.lastName || a.name || "").localeCompare(b.lastName || b.name || "");
  const departedMap = roster.departed || {};

  // Grid/dropdown staff = facility users + cross-facility roster additions.
  const crossDoctors = roster.supervisors.filter(
    (s) => s.crossFacility && !doctorPool.some((d) => d.id === s.id)
  );
  const crossCrnas = roster.crnas.filter(
    (c) => c.crossFacility && !providerPool.some((p) => p.id === c.id)
  );
  const allDoctors = [...doctorPool, ...crossDoctors];
  const allCrnas = [...providerPool, ...crossCrnas];

  // Grid dropdowns offer ONLY today's rostered people (the working list you
  // selected above), not the whole Anesthesia Staff pool. The AIC is a doctor
  // and may attend, so they're included in the doctor picker. Day values (call
  // order, shift time) live on the roster lists — merge them over the profile
  // docs so the sub-selects display what was picked instead of snapping empty.
  const rosterDoctorIds = new Set([roster.aicId, ...roster.supervisors.map((s) => s.id)].filter(Boolean));
  const rosterCrnaIds = new Set(roster.crnas.map((c) => c.id));
  const withDayFields = (people, rosterList) =>
    people.map((p) => ({ ...p, ...(rosterList.find((m) => m.id === p.id) || {}) }));
  const gridDoctors = withDayFields(allDoctors.filter((d) => rosterDoctorIds.has(d.id)), roster.supervisors);
  const gridCrnas = withDayFields(allCrnas.filter((c) => rosterCrnaIds.has(c.id)), roster.crnas);

  // Availability lists show the ROSTERED people (the day's working list),
  // enriched with profile callOrder/goHomeHour from the user docs.
  const enrich = (member, pool) => ({ ...pool.find((p) => p.id === member.id), ...member });
  const rosterDoctors = roster.supervisors.map((m) => enrich(m, allDoctors));
  // ⚠️ AND THE SECOND CRNA IN A ROOM, who may be on no roster doc at all.
  // Enrolment happens at IMPORT time, so a board built before that shipped
  // names them only on an assignment row — which is how three people who were
  // visibly working on the timeline were absent from this page's roster panel,
  // its availability list and its break board at once. Derived from the rows
  // this page already holds; idempotent, so a properly enrolled board is
  // unchanged.
  const rosterCrnas = mergeSecondProviders(roster.crnas, assignments).map((m) => enrich(m, allCrnas));

  // MD Call — every doctor on TODAY'S ROSTER (the AIC + every supervising
  // doctor you added), deduped, enriched with the day's callOrder. Adding a
  // doctor to the roster shows them here immediately; no grid assignment needed.
  const rosterDoctorEntries = [
    ...(roster.aicId ? [{ id: roster.aicId, name: roster.aicName }] : []),
    ...roster.supervisors,
  ];
  const mdCallDoctors = Array.from(
    new Map(rosterDoctorEntries.filter((m) => m.id).map((m) => [m.id, m])).values()
  )
    .map((m) => ({ ...allDoctors.find((d) => d.id === m.id), ...m }))
    .filter((d) => d.name);

  // Facility-wide MD call list from the sheet (code → name via shortCode). Each
  // entry carries callOrder / postCall / wc for the badge + colour. When the
  // sheet hasn't been imported, the panel falls back to the rostered doctors.
  const sheetMdCall = Array.isArray(roster.mdCallList) ? roster.mdCallList : [];
  // AIC badge follows the LIVE roster.aicId (sheet isAIC is import-time state
  // — after an AIC handoff the badge must move with the roster).
  const liveAicCode = String(
    allDoctors.find((d) => d.id === roster.aicId || d.uid === roster.aicId)?.shortCode || ""
  ).toUpperCase();
  const mdCallList = sheetMdCall.map((e) => ({
    ...e,
    name: allDoctors.find((d) => String(d.shortCode || "").toUpperCase() === e.code)?.name || e.code,
    isAIC: liveAicCode ? e.code === liveAicCode : Boolean(e.isAIC),
  }));

  const roomFor = (memberId) => rooms.find((r) => r.providerId === memberId);
  const rosterWithBreaks = [...rosterDoctors, ...rosterCrnas].map((m) => ({
    ...m,
    breaks: roomFor(m.id)?.breaks || {},
    departed: !!departedMap[m.id],
  }));

  const staffPool = { doctors: allDoctors, crnas: allCrnas };
  // Source facilityOptions from the domain list so the picker always offers
  // Wesley Chapel / Carrollwood / Tampa minus the current facility.
  const facilityOptions = otherFacilities(facilityId);

  // Two DIFFERENT truths, so two controls. Depart records that a real person
  // finished their shift and keeps them on the board in Home; Remove says they
  // were never on today's list. The page could previously only add a person or
  // wipe the entire day, so one name added by mistake had no way off.
  const departButton = (person) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <DepartureFlow
        person={person}
        assignments={assignments}
        roster={roster}
        aicId={roster.aicId}
        facilityId={facilityId}
        date={date}
        staffPool={staffPool}
        actor={actor}
      />
      <RemoveFromDay
        person={person}
        roster={roster}
        assignments={assignments}
        rooms={rooms}
        facilityId={facilityId}
        date={date}
        actor={actor}
        canManage={canManageDailyList}
      />
    </span>
  );

  return (
    <div style={s.page}>
      {/* TOPBAR */}
      <div style={s.topbar}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
          <span style={s.logo}>⚕ ATC</span>
          <NavMenu />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Avatar user={user} isDoctor={isDoctor} onClick={() => navigate("/profile")} />
          <SignOutButton />
        </div>
      </div>

      <div style={{ ...s.layout, flexDirection: isMobile ? "column" : "row" }}>
        {/* Navigation lives in the topbar NavMenu dropdown (no fixed sidebar). */}

        {/* MAIN */}
        <main style={s.main}>
          {/* 1 — Header */}
          <div style={{ ...s.boardHeader, flexWrap: "wrap" }}>
            <div>
              <h1 style={s.pageTitle}>Daily Assignments</h1>
              <div style={{ fontSize: font.size.body, color: color.text.secondary }}>
                Build the day's schedule, run breaks, and track departures — all in one place.
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: space.md, flexWrap: "wrap" }}>
              {/* Today / Tomorrow tabs — the two working days. The raw date
                  picker stays for history and further-out planning. */}
              <div role="tablist" aria-label="Working day" style={{ display: "flex", gap: 6, paddingBottom: 2 }}>
                {[
                  { key: rosterDateKey(), label: "Today" },
                  { key: nextDayKey(rosterDateKey()), label: "Tomorrow" },
                ].map((t) => (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={date === t.key}
                    onClick={() => setDate(t.key)}
                    style={{
                      border: `1px solid ${date === t.key ? color.accent.primary : color.border.default}`,
                      background: date === t.key ? color.accent.primary : color.surface.raised,
                      color: date === t.key ? "#fff" : color.text.secondary,
                      borderRadius: 9999, padding: "8px 16px", fontSize: 13,
                      fontWeight: 700, cursor: "pointer", fontFamily: "inherit", minHeight: 40,
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div>
                <label style={s.label} htmlFor="roster-date">Date</label>
                <input id="roster-date" type="date" style={{ ...s.input, width: 170 }}
                  value={date} onChange={(e) => setDate(e.target.value || rosterDateKey())} />
              </div>
              {/* Clear Roster for the Day — wipes the roster + every assignment
                  row for this date. Two-click confirm guards the destructive act. */}
              {confirmClear ? (
                <div style={{ display: "flex", gap: space.sm, alignItems: "center" }}>
                  <span style={{ fontSize: font.size.label, color: color.accent.error, fontWeight: font.weight.bold }}>Clear everything?</span>
                  <Button variant="danger" size="sm" onClick={clearRoster} loading={clearing}>Yes, clear</Button>
                  <Button variant="secondary" size="sm" onClick={() => setConfirmClear(false)} disabled={clearing}>Cancel</Button>
                </div>
              ) : (
                <Button variant="danger" onClick={() => setConfirmClear(true)}>Clear Roster for the Day</Button>
              )}
            </div>
          </div>

          {!isToday && (
            <div style={s.noteBanner}>
              You're editing <strong>{date}</strong>. Changes to a future day don't affect today's
              board or today's active AIC until that day arrives.
            </div>
          )}

          {/* Roster builder — who is working today (feeds grid + panels). */}
          <div style={{ ...s.panel, marginBottom: space.lg }}>
            <h2 style={s.panelTitle}>Today's Roster</h2>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: space.md }}>
              <div>
                <label style={s.label} htmlFor="aic-select">⭐ AIC (one doctor)</label>
                <div style={{ display: "flex", gap: space.sm }}>
                  <select id="aic-select" style={{ ...s.select, flex: 1 }} value={aicPick || roster.aicId || ""} disabled={saving}
                    onChange={(e) => setAicPick(e.target.value)}>
                    <option value="">Select the AIC…</option>
                    {/* AIC = one of THIS facility's own doctors. */}
                    {facilityDoctors.slice().sort(sortByLast).map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                  {/* Assign locks in the AIC for the day → daily_boards.aicId,
                      which AuthContext reads so every page sees the new AIC. */}
                  <Button size="sm" onClick={() => { setAIC(aicPick || roster.aicId || ""); setAicPick(""); }}
                    disabled={saving || (aicPick || roster.aicId || "") === (roster.aicId || "")}>
                    Assign
                  </Button>
                </div>
              </div>
              <div>
                <label style={s.label} htmlFor="sup-select">🩺 Doctors / Supervisors</label>
                <div style={{ display: "flex", gap: space.sm }}>
                  <select id="sup-select" style={{ ...s.select, flex: 1 }} value={supPick} disabled={saving}
                    onChange={(e) => setSupPick(e.target.value)}>
                    <option value="">Select a doctor…</option>
                    {/* Only this facility's doctors NOT already assigned today
                        (roster list + the AIC) — prevents double-assignment. */}
                    {facilityDoctors.slice().sort(sortByLast)
                      .filter((d) => d.id !== roster.aicId && !roster.supervisors.some((x) => x.id === d.id))
                      .map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <Button size="sm" onClick={addSupervisor} disabled={saving || !supPick}>＋ Add</Button>
                </div>
              </div>
              <div>
                <label style={s.label} htmlFor="crna-select">👥 CRNAs (CRNA · AA · Resident)</label>
                <div style={{ display: "flex", gap: space.sm }}>
                  <select id="crna-select" style={{ ...s.select, flex: 1 }} value={crnaPick} disabled={saving}
                    onChange={(e) => setCrnaPick(e.target.value)}>
                    <option value="">Select a CRNA…</option>
                    {/* Only this facility's CRNAs NOT already assigned today. */}
                    {facilityCrnas.slice().sort(sortByLast)
                      .filter((p) => !roster.crnas.some((x) => x.id === p.id))
                      .map((p) => <option key={p.id} value={p.id}>{p.name} · {(p.subRole || "crna").toUpperCase()}</option>)}
                  </select>
                  <Button size="sm" onClick={addCrna} disabled={saving || !crnaPick}>＋ Add</Button>
                </div>
              </div>
            </div>
          </div>

          {/* 2a — AI import surface (above grid; AIC/doctor-only). */}
          {canManageDailyList && isToday && (
            <AssignmentImport
              facilityId={facilityId}
              date={date}
              doctors={gridDoctors}
              crnas={gridCrnas}
              allDoctors={allDoctors}
              allCrnas={allCrnas}
              rooms={rooms}
              onApplyRows={onApplyRows}
              onAddStaff={async (person) => {
                const ref = await addStaff({ ...person, primaryFacilityId: facilityId });
                return { id: ref.id };
              }}
              recentImports={recentImports}
              onSaveImport={(record) =>
                saveSheetImport(facilityId, date, record)
                  .then(() => pruneSheetImportsKeepLast(facilityId, 3))
                  .catch((e) =>
                  console.error("[DailyAssignmentPage] save import failed:", e)
                )
              }
            />
          )}

          {/* 2b — GLOBAL import: one click writes all three facilities. Admin
              only (cross-facility writes are admin-gated by the rules). Has its
              OWN sheet config, separate from the facility-specific one above. */}
          {isAdmin && isToday && (
            <GlobalSheetImport date={date} allDoctors={allDoctors} allCrnas={allCrnas} />
          )}

          {/* 2 — Assignment grid + CRNA Call List side by side (≥900px), stacked below. */}
          <div style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: space.md,
            alignItems: "flex-start",
          }}>
            <div style={{ flex: 3, minWidth: 0 }}>
              <AssignmentGrid
                facilityId={facilityId}
                date={date}
                assignments={assignments}
                rooms={rooms}
                locations={locations}
                doctors={gridDoctors.filter((d) => !departedMap[d.id])}
                crnas={gridCrnas.filter((c) => !departedMap[c.id])}
                aicId={roster.aicId}
                actor={actor}
                roster={roster}
                onMemberPatch={onMemberPatch}
                showCrossFacilityButton={false}
                hiddenRooms={roster.hiddenRooms}
                onHideRoom={(room) => hideTemplateRoom(facilityId, date, room)}
                onUnhideRoom={(room) => unhideTemplateRoom(facilityId, date, room)}
              />
            </div>
            <div style={{ flex: 1, minWidth: 240, display: "flex", flexDirection: "column", gap: space.md }}>
              <MdCallPanel doctors={mdCallDoctors} callList={mdCallList.length ? mdCallList : null} facilityBoards={facilityBoards} facilities={allFacilities} />
              <CrnaRosterPanel crnas={rosterCrnas} />
            </div>
          </div>

          {/* 3 — Availability (Depart reachable from each person row). */}
          <div style={{ marginTop: space.xl }}>
            <AvailabilitySection
              doctors={rosterDoctors}
              crnas={rosterCrnas}
              aicId={roster.aicId}
              now={now}
              departed={departedMap}
              renderActions={departButton}
            />
          </div>

          {/* 4 — Break engine: compact at-a-glance board; click a name to open full controls. */}
          <div style={{ marginTop: space.xl }}>
            {(() => {
              // Shared row builder (useBreakBoardRows) — the mobile break board
              // renders from the same derivation. Pure form here because this
              // sits after the access-guard early return (rules of hooks).
              // assignments included: staffing lives on the day's ROWS, and a
              // sheet-imported board leaves the room docs unstaffed (which is
              // how every provider read "No room" with four dead cells).
              // ⚠️ `alerts` AND `nowMin`, OR THIS BOARD CANNOT SEE A REQUEST.
              // A break REQUEST lives in a BREAK_REQUEST alert, never in
              // `room.breaks` — `useBreakBoardRows` merges live alerts in for
              // exactly that reason. Omitting them left every cell grey while a
              // CRNA's own card said "Waiting for coverage…", so the one screen
              // a colleague opens to find someone to cover showed nothing to
              // cover. The shared builder was fixed; this call site was not.
              const activeProviders = breakBoardRows(
                rosterCrnas, departedMap, rooms, assignments, alerts, breakBoardNowMin
              );
              return activeProviders.length === 0 ? (
                <div>
                  <h2 style={s.sectionHeading}>Break Engine</h2>
                  {/* Says what is actually empty. "No break requests yet" was
                      a claim about the day; the list is empty because nobody is
                      on the CRNA roster, which is a different fact. */}
                  <p style={s.mutedNote}>Nobody is on today&rsquo;s CRNA roster.</p>
                </div>
              ) : (
                <BreakBoard providers={activeProviders} onOpenDetail={setBreakDetail} />
              );
            })()}
          </div>

          {/* Break detail modal — full BreakEnginePanel controls for the selected provider. */}
          {breakDetail && (() => {
            const room = roomFor(breakDetail.id);
            return (
              <Modal
                isOpen={!!breakDetail}
                onClose={() => setBreakDetail(null)}
                title={breakDetail.name}
                maxWidth={520}
              >
                <BreakEnginePanel
                  provider={breakDetail}
                  breaks={room?.breaks || initialBreaks()}
                  roomId={room?.id || null}
                  roster={rosterWithBreaks}
                  facilityId={facilityId}
                />
              </Modal>
            );
          })()}

          {/* 5 — Cross-facility staffing (last, per UI-SPEC section order). */}
          <div style={{ marginTop: space.xl, marginBottom: space["2xl"] }}>
            <Button variant="secondary" size={isMobile ? "full" : "md"} onClick={() => setPickerOpen(true)}>
              Add Staff from Another Facility
            </Button>
          </div>

          <CrossFacilityPicker
            isOpen={pickerOpen}
            onClose={() => setPickerOpen(false)}
            facilityOptions={facilityOptions}
            currentFacilityId={facilityId}
            date={date}
            actor={actor}
            roster={roster}
            floatAllowed={facilityRules.floatAllowed || isAdmin}
          />
        </main>
      </div>
    </div>
  );
}

// ── Styles — Phase-1 light token theme (D-10; UI-SPEC mapping table) ─────────
const s = {
  page:       { background: color.surface.base, color: color.text.primary, minHeight: "100vh", fontFamily: font.family, display: "flex", flexDirection: "column" },
  topbar:     { background: color.surface.card, borderBottom: `1px solid ${color.border.default}`, padding: "0 20px", height: 54, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 },
  logo:       { fontSize: 17, fontWeight: font.weight.bold, color: color.accent.primary },
  backBtn:    { background: "transparent", border: "none", color: color.text.secondary, cursor: "pointer", fontSize: 13 },
  avatar:     { width: 32, height: 32, borderRadius: "50%", background: color.accent.primary, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: font.weight.bold, fontSize: 12, color: "#fff" },
  layout:     { display: "flex", flex: 1, overflow: "hidden" },
  sidebar:    { width: 200, background: color.surface.card, borderRight: `1px solid ${color.border.default}`, padding: "12px 0", flexShrink: 0 },
  navItem:    { display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", cursor: "pointer", color: color.text.secondary, fontSize: 13, background: "transparent", border: "none", width: "100%", textAlign: "left", fontFamily: "inherit" },
  navActive:  { background: "#e8edf9", color: color.accent.primary, borderRight: `3px solid ${color.accent.primary}` },
  navLabel:   { padding: "16px 16px 6px", fontSize: 10, fontWeight: font.weight.bold, color: color.text.muted, letterSpacing: "1.2px", textTransform: "uppercase" },
  main:       { flex: 1, overflowY: "auto", padding: "22px 24px" },
  boardHeader:{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 20, marginBottom: 18 },
  pageTitle:  { fontSize: font.size.display, fontWeight: font.weight.bold, margin: "0 0 4px", lineHeight: font.lineHeight.tight },
  noteBanner: { background: "#e8edf9", border: `1px solid ${color.accent.primary}44`, borderRadius: 10, padding: "12px 16px", marginBottom: 18, fontSize: 13, color: color.text.secondary },
  panel:      { background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: 10, padding: 16 },
  panelTitle: { fontSize: font.size.body, fontWeight: font.weight.bold, margin: "0 0 14px" },
  sectionHeading: { fontSize: font.size.heading, fontWeight: font.weight.bold, margin: `0 0 ${space.md}px`, lineHeight: font.lineHeight.tight },
  label:      { display: "block", fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary, marginBottom: 6 },
  input:      { background: color.surface.card, border: `1px solid ${color.border.default}`, color: color.text.primary, borderRadius: 6, padding: "9px 12px", fontSize: 13, boxSizing: "border-box", fontFamily: font.family },
  select:     { width: "100%", background: color.surface.card, border: `1px solid ${color.border.default}`, color: color.text.primary, borderRadius: 6, padding: "9px 12px", fontSize: 13, fontFamily: font.family },
  mutedNote:  { fontSize: font.size.body, color: color.text.muted },
};
